import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {ColorDot} from '../../../../libs/shared/ui/src/components/utility/colorDot';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {inboxHasColor} from '../../../helpers/modelHelpers';

/*
 * Props.
 */

interface CustomAttributeInboxProps {
  inbox?: InboxModel;
  isCompact?: boolean;
  shouldAllowOverflow?: boolean;
}

/*
 * Style.
 */

interface StyledContentDivProps {
  $shouldAllowOverflow?: boolean;
}

const StyledContentDiv = styled.div<StyledContentDivProps>`
  display: flex;
  line-height: 16px;
  overflow: hidden;

  ${(p) =>
    p.$shouldAllowOverflow &&
    css`
      overflow: visible;
    `}
`;

interface StyledDotDivProps {
  $isCompact?: boolean;
}

const StyledDotDiv = styled.div<StyledDotDivProps>`
  margin-right: 10px;
  padding-top: 5px;

  ${(p) =>
    p.$isCompact &&
    css`
      margin-right: 6px;
    `}
`;

interface StyledNameDivProps {
  $shouldAllowOverflow?: boolean;
}

const StyledNameDiv = styled.div<StyledNameDivProps>`
  flex: 1;

  ${(p) =>
    !p.$shouldAllowOverflow &&
    css`
      ${ellipsis()}
    `}
`;

/*
 * Component.
 */

export const CustomAttributeInbox: FC<CustomAttributeInboxProps> = (props) => {
  const theme = useUserTheme();
  const {inbox, isCompact, shouldAllowOverflow} = props;
  if (!inbox) {
    return null;
  }

  const color = inboxHasColor(inbox) ? inbox.color : theme.greys.shade50;

  return (
    <StyledContentDiv $shouldAllowOverflow={shouldAllowOverflow}>
      <StyledDotDiv $isCompact={isCompact}>
        <ColorDot color={color} size={8} />
      </StyledDotDiv>
      <StyledNameDiv $shouldAllowOverflow={shouldAllowOverflow}>
        {renderName(inbox.name, shouldAllowOverflow)}
      </StyledNameDiv>
    </StyledContentDiv>
  );
};

/*
 * Helpers.
 */

function renderName(name: string, shouldAllowOverflow?: boolean) {
  if (shouldAllowOverflow) {
    return name;
  }
  return (
    <TooltipCoordinator render={() => name}>
      <TooltipOverflowCondition>{name}</TooltipOverflowCondition>
    </TooltipCoordinator>
  );
}
